<template>
  <div class="form">
    <div class="form__content">
      <h1 class="extra-large-headline">
        {{ $t("PageTitle.headline.verificationPageHeader") }}.
      </h1>
    </div>
    <form @submit.prevent>
      <EmailField
        v-model:email="form.email"
        :errors="store.getErrorByField('email')"
      />

      <MobileField
        v-if="showMobileFields()"
        v-model:mobile="form.mobile"
        @update:mobile="checkMobileFieldStatus"
        @dropdownValueUpdate="updateDropdownValues"
        :countries="countryList"
        :defaultValues="defaultValuesOnInt"
        :errors="store.getErrorByField('mobile')"
      />

      <AutocompleteSelect
        v-model:autocomplete="form.country"
        :label="$t('Autocomplete.label.countryResidence')"
        :placeholder="$t('MobileAutocompleteSelect.placeholder.phoneCountry')"
        :type="'text'"
        :items="countryList"
        :isLoading="isFetchingData"
        :errors="store.getErrorByField('country')"
        hasIcon
        @update:autocomplete="updateSelectedSingle"
      />

      <Checkbox
        name="marketing"
        :text="$t('Checkbox.text.marketing')"
        v-model:value="form.marketing"
        v-model:marketing="form.marketing"
      />

      <Checkbox
        name="tnc"
        :text="
          $t('Checkbox.text.tnc', {
            termOfUse: `<a target='_blank' href='https://banxa.com/terms-of-use/' rel='nofollow'>${$t(
              'Checkbox.text.termsAndCondition'
            )}</a>`,
            privacyPolicy: `<a target='_blank' href='https://banxa.com/privacy-policy/' rel='nofollow'>${$t(
              'Checkbox.text.privacyPolicy'
            )}</a>`,
          })
        "
        v-model:value="form.tnc"
        v-model:tnc="form.tnc"
        :errors="store.getErrorByField('tnc')"
      />

      <ButtonBase
        :isDisabled="store.formHasErrors || globalStore.getIsFormLoading"
        :isLoading="globalStore.getIsFormLoading"
        @onButtonClick="handleFormSubmit"
      >
        {{ $t("ButtonBase.label.submitVerification") }}
      </ButtonBase>
    </form>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { getCountryCallingCode } from "libphonenumber-js";

import { useGlobalStore } from "@/stores/globalStore";
import { useVerificationStore } from "@/stores/forms/verificationFormStore";
import { useOrderFormStore } from "@/stores/forms/orderFormStore";

import useWatchAndUpdate from "@/composables/helpers/useWatchAndUpdate";
import useApiHelpers from "@/composables/api/helpers";
import useProgressBarActions from "@/composables/progress/useProgressBarActions";
import useGlobalCountriesActions from "@/composables/store/global/useGlobalCountriesActions";
import { useIdentityQueueStore } from "@/stores/identityQueueStore";
import {
  isValueString,
  transformLinks,
} from "@/composables/helpers/useStringHelpers.js";
import useCustomerActions from "@/composables/api/useCustomerActions";

import AutocompleteSelect from "@/components/ui/selects/AutocompleteSelect.vue";
import EmailField from "@/components/ui/inputs/EmailField.vue";
import MobileField from "@/components/ui/inputs/MobileField.vue";
import Checkbox from "@/components/ui/inputs/Checkbox.vue";
import ButtonBase from "@/components/ui/buttons/ButtonBase.vue";

const route = useRoute();
const router = useRouter();
const store = useVerificationStore();
const globalStore = useGlobalStore();
const orderStore = useOrderFormStore();
const { fetchCountries } = useGlobalCountriesActions();
const { postRequest } = useApiHelpers();
const { watchAndUpdateStore } = useWatchAndUpdate();
const { updateToPending } = useProgressBarActions();
const identityQueueStore = useIdentityQueueStore();
const { fetchCustomerAccountInfo, fillForm } = useCustomerActions();
const form = ref({
  email: store.form.email.value || "",
  country: store.form.country.value || "United States",
  marketing: store.form.marketing.value || false,
  mobile: store.form.mobile?.value || "",
  internationalCode: store.form.internationalCode?.value || "+1",
  phoneCountry: store.form.phoneCountry?.value || "US",
  mobileCountry: store.form.country?.value || "United States",
  tnc: false,
});

// This is to track the status of the form fields
const formStatus = ref({
  country: {
    isTouched: false,
    isDirty: false,
  },
  mobile: {
    isTouched: false,
    isDirty: false,
  },
});

const defaultValuesOnInt = ref({});
const countryList = ref([]);
const isFetchingData = ref(true);

/** Checks if either Mobile Country or Mobile number is updated */
const checkMobileFieldStatus = (data) => {
  formStatus.value.mobile.isTouched = true;
  formStatus.value.mobile.isDirty = data !== "" && data !== undefined;
};

const setMobileFieldValue = () => {
  const phoneCountry =
    store.form.phoneCountry?.value || form.value.phoneCountry;
  const internationalCode = (
    store.form.internationalCode?.value || form.value.internationalCode
  ).replace(/\+{2,}/g, "+"); // HE-1077: Remove the extra + sign
  const country = store.form.country?.value || form.value.country;
  const mobile = store.form.mobile?.value || form.value.mobile;
  const mobileCountry = countryList.value.find(
    ({ code }) => code === phoneCountry
  ).name;
  return { internationalCode, phoneCountry, country, mobile, mobileCountry };
};

/**
 * This is invoked when there is change on the form
 * @param {Object} data
 * @param {String} from
 */
const setFormData = (data, from) => {
  const { name } = data;

  if (
    from === "country" ||
    (from === "mobile" && !formStatus.value.country.isDirty)
  ) {
    formStatus.value.country.isTouched = true;

    if (from === "country") {
      formStatus.value.country.isDirty = true;
    }

    form.value.country = name;
  }

  // Only checks if the Mobile Country is updated
  if (from === "mobile") {
    form.value.mobileCountry = name;
    const filteredCountry = countryList.value.find(
      (country) => country.name === name
    );
    form.value.phoneCountry = filteredCountry.code;
    form.value.internationalCode = `+${getCountryCallingCode(
      form.value.phoneCountry
    )}`.replace(/\+{2,}/g, "+");

    // HE-1077: As we update the component's form object, we update the local storage's form object as well
    store.updateValue("phoneCountry", form.value.phoneCountry);
    store.updateValue("internationalCode", form.value.internationalCode);
    formStatus.value.mobile.isTouched = true;
    formStatus.value.mobile.isDirty = true;
  }

  /** Reset the mobile field values */
  defaultValuesOnInt.value = setMobileFieldValue();
};

/** This is invoked when the Mobile Country is changed. */
const updateDropdownValues = (dropdownData) => {
  const selectedCountry = countryList.value.find(
    (country) => country.code === dropdownData.code
  );
  setFormData(selectedCountry, "mobile");
};

const isEnableMobile = () => {
  return (
    globalStore.getMerchantSettings.VALIDATE_MSISDN ||
    orderStore.getPaymentObject.require_mobile ||
    (!globalStore.getMerchantSettings.VALIDATE_MSISDN &&
      !globalStore.getMerchantSettings.VALIDATE_EMAIL)
  );
};

const updateSelectedSingle = (selectedItem) => {
  if (isEnableMobile() && countryList.value.length) {
    const selectedCountry = countryList.value.find(
      (country) => country.name === selectedItem.value
    );
    setFormData(selectedCountry, "country");
  }
};

const handleFormRedirect = (response) => {
  const { data } = response;
  const needToVerifyMobile =
    isValueString(data.rel.next) && data.rel.next === "UserMustVerifyMobile";

  if (needToVerifyMobile || data?.rel?.route === "identification.update") {
    router.push("/verification/code");
  } else {
    // Check if anything is required to submit the identity
    identityQueueStore.fetchNextIdentityRoute(route).then((nextRoute) => {
      router.push(nextRoute);
    });
  }
};

const showMobileFields = () => {
  /** @type {boolean} */
  const isMobileEnabled = isEnableMobile();

  if (isMobileEnabled) {
    store.reAddFields("internationalCode", "phoneCountry", "mobile");
  } else {
    store.removeFields("internationalCode", "phoneCountry", "mobile");
  }

  return isMobileEnabled;
};

const handleFormSubmit = () => {
  globalStore.setIsFormLoading(true);
  const { phoneCountry, mobile, internationalCode, ...rest } = form.value;
  if (!showMobileFields()) {
    postRequest("/accounts", rest, handleFormRedirect);
    return;
  }
  postRequest("/accounts", form.value, handleFormRedirect);
};

onMounted(async () => {
  updateToPending("verification");

  const formattedCountries = await fetchCountries();

  countryList.value = formattedCountries;

  const customerInfo = await fetchCustomerAccountInfo();
  await fillForm(customerInfo, form, store);

  isFetchingData.value = false;

  if (isEnableMobile()) {
    defaultValuesOnInt.value = setMobileFieldValue();
  }

  /**
   * HE-1077
   * This is to fix a mobile country field bug where the user switches between payment methods, where we basically reset it to default here.
   * Preventing Australia (+1) or United States (+61) on mobile country field which the system is confused between this
   * component's form object and the store's form object.
   */
  if (defaultValuesOnInt.value.country) {
    const initialCountry = countryList.value.find(
      (country) => country.code === defaultValuesOnInt.value.phoneCountry
    );
    form.value.country = initialCountry.name;
    defaultValuesOnInt.value.country = form.value.country;
  }

  if (defaultValuesOnInt.value.phoneCountry) {
    form.value.phoneCountry = defaultValuesOnInt.value.phoneCountry;
    form.value.internationalCode = `+${getCountryCallingCode(
      defaultValuesOnInt.value.phoneCountry
    )}`;
    form.value.mobileCountry = defaultValuesOnInt.value.mobileCountry;
  }

  if (orderStore.isNFTMode) {
    form.value.consumerReport = true;
  }
});

onUnmounted(() => {
  // Clean all form status for each fields.
  Object.keys(formStatus.value).forEach((key) => {
    formStatus.value[key] = {
      isTouched: false,
      isDirty: false,
    };
  });
});

watchAndUpdateStore(form, store, (key, val) => {
  if (key === "phoneCountry") {
    store.updateValue("mobile", form.value.mobile);
  }

  if (key === "country") {
    store.updateValue("country", form.value.country);
  }
});
</script>
